import React, { memo, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Divider,
  Grid,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import GroupsIcon from "@mui/icons-material/Groups";
import AssessmentIcon from "@mui/icons-material/Assessment";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import WorkIcon from "@mui/icons-material/Work";
import DiversityIcon from "@mui/icons-material/Diversity3";
import styles from "./talent-report-agent.module.scss";

type TalentReportAgentProps = {
  parsedContent: any;
};

type CandidateSource = {
  total: number;
  [key: string]: number;
};

type FunnelSizeMetrics = {
  internal_candidates: CandidateSource;
  external_candidates: CandidateSource;
};

type FunnelQualityMetrics = {
  required_skills_match_rate: string;
  skill_adjacent: string;
  ready_now_availability: string;
  dei_gender_target: string;
};

type TalentReportData = {
  total_candidates_sourced: number;
  qualified_candidates: number;
  qualified_candidates_average_score: number;
  funnel_size_metrics: FunnelSizeMetrics;
  funnel_quality_metrics: FunnelQualityMetrics;
  excel_download_url: string;
};

const MetricCard = memo<{
  icon: React.ReactNode;
  value: string | number;
  label: string;
  subtext?: string;
}>(({ icon, value, label, subtext }) => (
  <Card className={styles.metricCard}>
    <CardContent className={styles.metricCardContent}>
      {icon}
      <Typography className={styles.metricValue}>{value}</Typography>
      <Typography className={styles.metricLabel}>{label}</Typography>
      {subtext && (
        <Typography className={styles.metricSubtext}>{subtext}</Typography>
      )}
    </CardContent>
  </Card>
));

MetricCard.displayName = "MetricCard";

const FunnelMetricBox = memo<{
  icon: React.ReactNode;
  value: number;
  label: string;
  breakdown?: Array<{ label: string; value: number; icon?: React.ReactNode }>;
}>(({ icon, value, label, breakdown }) => (
  <Box className={styles.funnelMetricBox}>
    <Box className={styles.funnelMetricHeader}>
      {icon}
      <Box className={styles.funnelMetricContent}>
        <Typography className={styles.funnelMetricValue}>{value}</Typography>
        <Typography className={styles.funnelMetricLabel}>{label}</Typography>
      </Box>
    </Box>
    {breakdown && breakdown.length > 0 && (
      <Box className={styles.funnelBreakdown}>
        <Divider sx={{ my: 1.5 }} />
        {breakdown.map((item, idx) => (
          <Box key={idx} className={styles.breakdownItem}>
            {item.icon && (
              <Box className={styles.breakdownIcon}>{item.icon}</Box>
            )}
            <Typography className={styles.breakdownLabel}>
              {item.label}
            </Typography>
            <Typography className={styles.breakdownValue}>
              {item.value}
            </Typography>
          </Box>
        ))}
      </Box>
    )}
  </Box>
));

FunnelMetricBox.displayName = "FunnelMetricBox";

const QualityMetricItem = memo<{
  dotColor: string;
  title: string;
  value: string;
}>(({ dotColor, title, value }) => (
  <Box className={styles.qualityMetricItem}>
    <Box className={styles.qualityMetricHeader}>
      <Box className={styles.qualityMetricDot} sx={{ bgcolor: dotColor }} />
      <Typography className={styles.qualityMetricTitle}>{title}</Typography>
    </Box>
    <Typography className={styles.qualityMetricValue}>{value}</Typography>
  </Box>
));

QualityMetricItem.displayName = "QualityMetricItem";

const TalentReportAgent: React.FC<TalentReportAgentProps> = ({
  parsedContent,
}) => {
  const reportData = useMemo<TalentReportData | null>(() => {
    try {
      const finalAnswer = parsedContent?.finalAnswer;
      if (Array.isArray(finalAnswer) && finalAnswer.length > 0) {
        return JSON.parse(finalAnswer[0]);
      } else if (typeof finalAnswer === "string") {
        return JSON.parse(finalAnswer);
      } else if (parsedContent?.total_candidates_sourced !== undefined) {
        return parsedContent as TalentReportData;
      }
    } catch (error) {
      console.error("Error parsing talent report data:", error);
    }
    return null;
  }, [parsedContent]);

  const qualificationRate = useMemo(() => {
    if (!reportData || reportData.total_candidates_sourced === 0) return "0";
    return (
      (reportData.qualified_candidates / reportData.total_candidates_sourced) *
      100
    ).toFixed(0);
  }, [reportData]);

  const handleDownload = useCallback(() => {
    if (reportData?.excel_download_url) {
      window.open(
        reportData.excel_download_url,
        "_blank",
        "noopener,noreferrer",
      );
    }
  }, [reportData?.excel_download_url]);

  if (!reportData) {
    return (
      <Box className={styles.errorContainer}>
        <Typography color="error">
          Unable to load talent scouting report. Please try again.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={styles.talentReportContainer}>
      {/* Header Section */}
      <Box className={styles.headerSection}>
        <Box className={styles.headerIcon}>
          <AssessmentIcon sx={{ fontSize: 32, color: "#00a888" }} />
        </Box>
        <Typography className={styles.headerTitle}>
          Talent Scouting Report
        </Typography>
        <Typography className={styles.headerSubtitle}>
          Comprehensive overview of candidate pipeline and quality metrics
        </Typography>
      </Box>

      {/* Key Metrics Cards */}
      <Grid container spacing={2} className={styles.metricsGrid}>
        <Grid item xs={12} sm={6}>
          <MetricCard
            icon={
              <Box className={styles.metricIcon} sx={{ bgcolor: "#e8f5f3" }}>
                <PeopleIcon sx={{ fontSize: 24, color: "#00a888" }} />
              </Box>
            }
            value={reportData.total_candidates_sourced}
            label="Total Candidates"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <MetricCard
            icon={
              <Box className={styles.metricIcon} sx={{ bgcolor: "#d1fae5" }}>
                <CheckCircleOutlineIcon
                  sx={{ fontSize: 24, color: "#10b981" }}
                />
              </Box>
            }
            value={reportData.qualified_candidates}
            label="Matched Candidates"
            subtext={`${qualificationRate}% of total`}
          />
        </Grid>
      </Grid>

      {/* Funnel Size Metrics Section */}
      <Box className={styles.sectionBox}>
        <Typography className={styles.sectionTitle}>
          Candidate Sourcing Breakdown
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FunnelMetricBox
              icon={
                <Box
                  className={styles.funnelMetricIcon}
                  sx={{ bgcolor: "#ede9fe" }}
                >
                  <BusinessCenterIcon sx={{ fontSize: 20, color: "#7c3aed" }} />
                </Box>
              }
              value={
                typeof reportData.funnel_size_metrics.internal_candidates ===
                "object"
                  ? reportData.funnel_size_metrics.internal_candidates.total
                  : reportData.funnel_size_metrics.internal_candidates || 0
              }
              label="Internal Candidates"
              breakdown={
                typeof reportData.funnel_size_metrics.internal_candidates ===
                  "object" &&
                reportData.funnel_size_metrics.internal_candidates !== null
                  ? Object.entries(
                      reportData.funnel_size_metrics.internal_candidates,
                    )
                      .filter(([key]) => key !== "total")
                      .map(([key, value]) => ({
                        label:
                          key.charAt(0).toUpperCase() +
                          key.slice(1).replace(/_/g, " "),
                        value: value as number,
                      }))
                  : undefined
              }
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FunnelMetricBox
              icon={
                <Box
                  className={styles.funnelMetricIcon}
                  sx={{ bgcolor: "#e0f2fe" }}
                >
                  <PersonAddIcon sx={{ fontSize: 20, color: "#0284c7" }} />
                </Box>
              }
              value={
                typeof reportData.funnel_size_metrics.external_candidates ===
                "object"
                  ? reportData.funnel_size_metrics.external_candidates.total
                  : reportData.funnel_size_metrics.external_candidates || 0
              }
              label="External Candidates"
              breakdown={
                typeof reportData.funnel_size_metrics.external_candidates ===
                  "object" &&
                reportData.funnel_size_metrics.external_candidates !== null
                  ? Object.entries(
                      reportData.funnel_size_metrics.external_candidates,
                    )
                      .filter(([key]) => key !== "total")
                      .map(([key, value]) => ({
                        label:
                          key.charAt(0).toUpperCase() +
                          key.slice(1).replace(/_/g, " "),
                        value: value as number,
                      }))
                  : undefined
              }
            />
          </Grid>
        </Grid>
      </Box>

      {/* Funnel Quality Metrics Section */}
      <Box className={styles.sectionBox}>
        <Typography className={styles.sectionTitle}>
          Candidate Pool Quality
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Box className={styles.qualityMetricsContainer}>
          {reportData.funnel_quality_metrics.required_skills_match_rate &&
            reportData.funnel_quality_metrics.required_skills_match_rate.trim() !==
              "" && (
              <QualityMetricItem
                dotColor="#10b981"
                title="High-Fit Candidates"
                value={
                  reportData.funnel_quality_metrics.required_skills_match_rate
                }
              />
            )}

          {reportData.funnel_quality_metrics.skill_adjacent &&
            reportData.funnel_quality_metrics.skill_adjacent.trim() !== "" && (
              <QualityMetricItem
                dotColor="#f59e0b"
                title="Transferable Candidates"
                value={reportData.funnel_quality_metrics.skill_adjacent}
              />
            )}

          {reportData.funnel_quality_metrics.ready_now_availability &&
            reportData.funnel_quality_metrics.ready_now_availability.trim() !==
              "" && (
              <QualityMetricItem
                dotColor="#3b82f6"
                title="Immediate Availability"
                value={reportData.funnel_quality_metrics.ready_now_availability}
              />
            )}

          {reportData.funnel_quality_metrics.dei_gender_target &&
            reportData.funnel_quality_metrics.dei_gender_target.trim() !==
              "" && (
              <QualityMetricItem
                dotColor="#ec4899"
                title="DEI Gender Target"
                value={reportData.funnel_quality_metrics.dei_gender_target}
              />
            )}
        </Box>
      </Box>

      {/* Download Button */}
      <Box className={styles.downloadSection}>
        <Button
          variant="contained"
          onClick={handleDownload}
          startIcon={<DownloadIcon />}
          className={styles.downloadButton}
        >
          Download Full Report (Excel)
        </Button>
      </Box>
    </Box>
  );
};

export default memo(TalentReportAgent);
